<template>
  <b-modal
    id="product-variation-modal"
    :title="titleTextDisplay"
    footer-class="border-top py-3"
    content-class="p-0"
    centered
    scrollable
    :size="is_manage ? 'lg' : null"
    @shown="initForm"
    @hide="$parent.is_manage = false"
  >
    <b-form>
      <div
        v-for="(variant, variantIndex) in form"
        :key="`variant-name-${variantIndex}`"
        :class="{ 'bg-light rounded border mb-4 p-3': is_manage }"
      >
        <b-form-group
          label-class="product-creation-labels"
          class="mb-4"
          :state="validateState(variantIndex, 'name')"
          :invalid-feedback="formErrors[variantIndex].name"
        >
          <template slot="label">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                Variant Name <span class="font-weight-bold text-danger">*</span>
              </div>
              <img
                v-if="form.length > 1 && !variant.uuid"
                :id="`delete-variant-${variantIndex}`"
                src="@/assets/images/actions/delete.svg"
                :alt="`Delete Variant Option ${variantIndex}`"
                style="cursor: pointer"
                @click="deleteAttribute(variantIndex)"
              />
            </div>
          </template>
          <b-form-input id="variant-name" v-model="variant.name" maxlength="40">
          </b-form-input>
        </b-form-group>
        <b-form-group
          label-class="product-creation-labels"
          v-for="(option, optionIndex) in variant.options"
          :key="`variant_option_${optionIndex}`"
          :state="validateState(variantIndex, 'options', optionIndex)"
          :invalid-feedback="formErrors[variantIndex].options[optionIndex].name"
        >
          <template slot="label">
            Variant Option <span class="font-weight-bold text-danger">*</span>
          </template>
          <b-input-group>
            <b-form-input
              :id="`variant-option-${optionIndex}`"
              v-model="option.name"
              :class="{
                'border-right-0':
                  variant.options.length > 1 && option.action === 'create',
              }"
              maxlength="100"
            >
            </b-form-input>
            <template
              slot="append"
              v-if="variant.options.length > 1 && option.action === 'create'"
            >
              <b-input-group-text
                class="form-control bg-transparent border-left-0"
              >
                <img
                  :id="`delete-variant-option-${optionIndex}`"
                  src="@/assets/images/actions/delete.svg"
                  :alt="`Delete Variant Option ${optionIndex}`"
                  @click="deleteOption(variantIndex, optionIndex)"
                />
              </b-input-group-text>
            </template>
          </b-input-group>
        </b-form-group>
        <b-form-group>
          <b-button
            variant="ghost"
            class="text-primary border font-weight-bold create-button px-3"
            @click="addOption(variantIndex)"
          >
            + Add Option
          </b-button>
        </b-form-group>
      </div>
    </b-form>
    <template slot="modal-footer">
      <b-button variant="ghost" class="btn-cancel" @click="close"
        >Cancel</b-button
      >
      <b-button variant="primary" @click="addVariant">Save</b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import { required } from "vuelidate/lib/validators";
import { DateTime } from "luxon";

function initializeForm() {
  return [
    {
      name: null,
      action: "create",
      options: [{ name: null, action: "create" }],
      uuid: null,
    },
  ];
}

const { millisecond } = DateTime.now();

export default {
  props: {
    is_manage: Boolean,
  },
  data() {
    return {
      form: initializeForm(),
    };
  },
  computed: {
    ...mapState("products", ["product_attributes", "product_variables"]),
    titleTextDisplay() {
      if (this.is_manage) return "Manage Variants";
      else return "Create Variant";
    },
    formErrors() {
      let errors = this.form.map((variant) => {
        return {
          name: null,
          options: variant.options.map(() => {
            return {
              name: null,
            };
          }),
        };
      });

      const validations = {
        name: ["required", "unique"],
        options: ["required"],
      };

      const validation_messages = {
        required: "This field is required.",
        unique: "The name was already used. All names must be unique.",
      };

      Object.keys(validations).forEach(() => {
        let variations;
        if (this.is_manage)
          variations = this.form
        else 
          variations = this.product_attributes;
        let variation_names = [];
        for (let i=0; i<variations.length; i++) {
          if (variations[i].name)
            variation_names[i] =  variations[i].name.trim()
        }
        this.form.map((variation, variationIndex) => {
          Object.keys(variation).forEach((field) => {
            if (
              field === "name" &&
              this.$v.form.$each[variationIndex][field].$dirty &&
              !this.$v.form.$each[variationIndex][field].required
            )
              errors[variationIndex][field] = validation_messages.required;
            else if (
              field === "name" && variation.name
            ) {
              let exist = variation_names.indexOf(variation.name.trim());
              if ((!this.is_manage && exist != -1)|| (this.is_manage && exist != variationIndex))
                errors[variationIndex][field] = validation_messages.unique;
            }
            else if (field === "options")
              this.form[variationIndex][field].forEach(
                (option, optionIndex) => {
                  if (
                    this.$v.form.$each[variationIndex][field].$each[
                      optionIndex
                    ]["name"].$dirty &&
                    !this.$v.form.$each[variationIndex][field].$each[
                      optionIndex
                    ]["name"].required
                  )
                    errors[variationIndex][field][optionIndex]["name"] =
                      validation_messages.required;
                }
              );
          });
        });
      });

      return errors;
    },
  },
  validations: {
    form: {
      $each: {
        name: {
          required,
        },
        options: {
          $each: {
            name: {
              required,
            },
          },
        },
      },
    },
  },
  methods: {
    initForm() {
      if (this.is_manage) {
        this.form = this.product_attributes.map((variant) => {
          return {
            action: variant.action,
            uuid: variant.uuid,
            name: variant.name,
            options: variant.variations,
          };
        });
      } else this.form = initializeForm();

      this.$v.$reset();
    },
    close() {
      this.$parent.is_manage = false;

      this.$root.$emit("bv::hide::modal", "product-variation-modal");
    },
    addOption(variantIndex) {
      this.form[variantIndex].options.push({
        name: null,
        action: "create",
        hash: null,
      });
    },
    deleteOption(variantIndex, optionIndex) {
      this.form[variantIndex].options.splice(optionIndex, 1);
    },
    deleteAttribute(variantIndex) {
      this.form.splice(variantIndex, 1);
    },
    addVariant() {
      this.$v.form.$touch();

      let variations;
      variations = this.form
      if (!this.is_manage)
        variations = variations.concat(this.product_attributes);
      let variation_names = [];
      for (let i=0; i<variations.length; i++) {
        if (variations[i].name)
          variation_names[i] = variations[i].name.trim()
      }
      let unique = [...new Set(variation_names)];
      let has_duplicates = false;
      if (variation_names.length != unique.length)
        has_duplicates = true;

      if (!this.$v.form.$invalid && !has_duplicates) {
        const attrLength = this.product_attributes.length;

        this.$nextTick(() => {
          if (this.is_manage) this.manageVariants();
          else this.addVariants(attrLength);
        });
      }
    },
    addVariants(attrLength) {
      const variant = {
        name: this.form[0].name,
        action: this.form[0].action,
        variations: this.form[0].options.map((option, optionIndex) => ({
          action: option.action,
          name: option.name,
          hash: `${option.name
            .replace(/[^a-zA-Z0-9]/g, "")
            .slice(0, 4)
            .toUpperCase()}-${attrLength}${optionIndex}${millisecond}`,
        })),
      };
      this.$store.commit("products/pushProductAttributes", variant);
      this.close();
    },
    manageVariants() {
      const variations = this.form.map((variation, variationIndex) => {
        return {
          name: variation.name,
          action: variation.action,
          uuid: variation.uuid,
          variations: variation.options.map((option, optionIndex) => ({
            action: option.action,
            name: option.name,
            hash: option.hash
              ? option.hash
              : `${option.name
                  .replace(/[^a-zA-Z0-9]/g, "")
                  .slice(0, 4)
                  .toUpperCase()}-${variationIndex}${optionIndex}${millisecond}`,
          })),
        };
      });

      this.$store.commit("products/setProductAttributes", variations);
      this.close();
    },
    validateState(variationIndex, field, optionIndex = null) {
      if (optionIndex) {
        if (this.formErrors[variationIndex][field][optionIndex]["name"])
          return false;

        const { $dirty, $error } =
          this.$v.form.$each[variationIndex][field].$each[optionIndex].name;

        return $dirty ? !$error : null;
      } else {
        if (this.formErrors[variationIndex][field]) return false;

        const { $dirty, $error } = this.$v.form.$each[variationIndex][field];

        return $dirty ? !$error : null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.btn.create-button {
  border-radius: 20px;
  font-size: 15px;
}
[id^="delete-variant-option-"] {
  cursor: pointer;
}
</style>
